/**
 * Caregiver Report API.
 *
 * Same report Lambda and the same body shape as `fetchEventReport` in
 * alert-details/api/alertDetailsApi.js — only `rep_type` differs. Deliberately
 * parallel: the two reports read the same event population through the same
 * filters, so a divergence in what gets sent would be a divergence in what the
 * two reports mean.
 *
 * Filter options and reason codes are NOT re-fetched here. `fetchFilterOptions`
 * from the alert-details module already returns everything the shared
 * ReportFilterDrawer needs, and a second copy would drift.
 */

import { postReport } from '../../../../services/api.js';
import { reportFilterFields, responseTimeField, timeWindowField } from '../../../../utils/reportRequestBody.js';

/**
 * Run the caregiver report.
 *
 * @param {object} opts
 * @param {number} opts.campusId
 * @param {string} opts.username
 * @param {string} opts.startDate — 'YYYY-MM-DD' or 'YYYY-MM-DD HH:MM:SS'
 * @param {string} opts.endDate
 * @param {object} opts.filters   — the page's report-level filter selections
 * @param {boolean} opts.isCloud  — campus is on 5.0
 * @param {string} opts.campusGuid — core_campus.campus_guid (used when not cloud)
 * @returns {Promise<object>} { records, totals, summary, overlimit, url, ErrorCode }
 */
export async function fetchCaregiverReport({ campusId, username, startDate, endDate, filters = {}, isCloud = false, campusGuid = '' }) {
    const body = {
        rep_type: 'caregiverReport',
        /* Both decide where last_login_time comes from: core_user on a cloud campus,
           DynamoDB V2User (queried by campus_guid) on a non-cloud one. The Lambda
           defaults is_cloud to falsey and campus_guid to '', and with those the Dynamo
           lookup finds nothing and silently falls back to the MySQL value -- which on
           a non-cloud campus is the wrong store. The scheduled email path
           (report_generator.py) already sends both; this is the on-screen twin. */
        is_cloud: isCloud ? 1 : 0,
        campus_guid: campusGuid || '',
        username: username || '',
        campus_id: campusId,
        start_date: startDate,
        end_date: endDate,
        /* Empty on purpose. The event report sends "1,4,10,11,12,13,15"; this
           report counts every category a caregiver acknowledged, maintenance
           included, and the backend reads '' as "no category filter". */
        event_category_filter: '',
        ...reportFilterFields(filters),
        resp_filter_items: responseTimeField(filters),
        // Minutes from midnight, half-open. [0, 1440] is all day and the query
        // treats it as a no-op, so an omitted window changes nothing.
        time_window: timeWindowField(filters),
    };

    const res = await postReport(body, '[fetchCaregiverReport] request failed');
    if (res.ErrorCode) return { ErrorCode: res.ErrorCode };
    const data = res.data;

    return {
        records: Array.isArray(data?.records) ? data.records : [],
        totals: Array.isArray(data?.totals) ? data.totals : [],
        summary: data?.summary || {},
        overlimit: Boolean(data?.overlimit),
        url: data?.url || '',
    };
}
